/**
 * Asset Service - Centralized configuration for design assets
 * Handles URLs, fallbacks, and environment-specific configurations
 */

class AssetService {
  constructor() {
    this.baseUrl = this.getBackendUrl();
    this.assetsPath = '/api/design-assets';
    
    // Asset configurations with fallbacks
    this.assetConfig = {
      'tamara-banner': {
        name: 'Tamara LinkedIn Banner',
        description: 'Professional LinkedIn banner for Tamara Navarrete',
        fallback: {
          title: 'Tamara Navarrete',
          subtitle: 'Estratega de M&A y Desarrollo de Negocio',
          description: 'Especializada en valoración de marcas y estrategias de crecimiento'
        },
        cache: true,
        timeout: 8000
      },
      'linkedin-banner': {
        name: 'LinkedIn Banner Template',
        description: 'Generic LinkedIn banner template',
        fallback: {
          title: 'LinkedIn Banner',
          subtitle: 'Professional Template',
          description: 'Custom LinkedIn banner design'
        },
        cache: true,
        timeout: 6000
      },
      'design-guide': {
        name: 'Design Guidelines',
        description: 'Brand design guidelines and standards',
        fallback: {
          title: 'Design Guidelines',
          subtitle: 'Brand Standards',
          description: 'Brand identity and design specifications'
        },
        cache: false,
        timeout: 5000
      }
    };
  }

  /**
   * Get backend URL from environment with fallback
   */
  getBackendUrl() {
    const url = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    
    if (!url) {
      console.warn('⚠️  REACT_APP_BACKEND_URL not configured, asset service may not work');
      return '';
    }
    
    return url.replace(/\/$/, ''); // Remove trailing slash
  }

  /**
   * Get full URL for asset
   */
  getAssetUrl(assetName) {
    if (!this.baseUrl) {
      console.error('❌ Backend URL not configured');
      return null;
    }
    
    return `${this.baseUrl}${this.assetsPath}/${assetName}`;
  }

  /**
   * Get download URL for asset
   */
  getDownloadUrl(assetName) {
    if (!this.baseUrl) {
      console.error('❌ Backend URL not configured');
      return null;
    }
    
    return `${this.baseUrl}${this.assetsPath}/download/${assetName}`;
  }

  /**
   * Get asset configuration
   */
  getAssetConfig(assetName) {
    return this.assetConfig[assetName] || {
      name: assetName,
      description: `Asset: ${assetName}`,
      fallback: {
        title: 'Asset Not Found',
        subtitle: 'Configuration Missing',
        description: `Asset "${assetName}" configuration not found`
      },
      cache: true,
      timeout: 5000
    };
  }

  /**
   * Check if asset is configured
   */
  isAssetConfigured(assetName) {
    return !!this.assetConfig[assetName];
  }

  /**
   * Get all available assets
   */
  getAvailableAssets() {
    return Object.keys(this.assetConfig);
  }

  /**
   * Test asset availability
   */
  async testAssetAvailability(assetName, timeout = 5000) {
    const url = this.getAssetUrl(assetName);
    
    if (!url) {
      return {
        available: false,
        error: 'Backend URL not configured',
        status: null,
        responseTime: null
      };
    }

    const startTime = Date.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(url, {
        method: 'HEAD', // Use HEAD for faster response
        headers: {
          'Accept': '*/*',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      
      return {
        available: response.ok,
        error: response.ok ? null : `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
        responseTime
      };
      
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      return {
        available: false,
        error: error.name === 'AbortError' ? 'Request timeout' : error.message,
        status: null,
        responseTime
      };
    }
  }

  /**
   * Test all configured assets
   */
  async testAllAssets(timeout = 8000) {
    const assetNames = this.getAvailableAssets();
    const results = {};
    
    console.log(`🔍 Testing ${assetNames.length} configured assets...`);
    
    const testPromises = assetNames.map(async (assetName) => {
      const result = await this.testAssetAvailability(assetName, timeout);
      results[assetName] = result;
      
      if (result.available) {
        console.log(`✅ Asset '${assetName}' available (${result.responseTime}ms)`);
      } else {
        console.warn(`❌ Asset '${assetName}' unavailable: ${result.error}`);
      }
      
      return result;
    });
    
    await Promise.all(testPromises);
    
    const availableCount = Object.values(results).filter(r => r.available).length;
    console.log(`📊 Asset availability: ${availableCount}/${assetNames.length} available`);
    
    return results;
  }

  /**
   * Generate fallback content for asset
   */
  generateFallbackContent(assetName, type = 'html') {
    const config = this.getAssetConfig(assetName);
    const fallback = config.fallback;
    
    if (type === 'html') {
      return `
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${fallback.title}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              padding: 20px;
            }
            .container {
              text-align: center;
              max-width: 600px;
              padding: 40px;
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(10px);
              border-radius: 16px;
              border: 1px solid rgba(255, 255, 255, 0.2);
            }
            h1 { font-size: 2.5rem; margin-bottom: 8px; }
            .subtitle { font-size: 1.2rem; opacity: 0.9; margin-bottom: 20px; }
            .description { font-size: 1rem; opacity: 0.8; line-height: 1.6; }
            .notice { 
              margin-top: 30px; 
              padding: 15px; 
              background: rgba(255, 255, 255, 0.1); 
              border-radius: 8px; 
              font-size: 0.9rem;
              opacity: 0.7;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${fallback.title}</h1>
            <div class="subtitle">${fallback.subtitle}</div>
            <div class="description">${fallback.description}</div>
            <div class="notice">
              ⚠️ Este contenido se está mostrando como respaldo.<br>
              El recurso original no está disponible temporalmente.
            </div>
          </div>
        </body>
        </html>
      `;
    }
    
    return fallback;
  }
}

// Export singleton instance
export const assetService = new AssetService();
export default assetService;